import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BarChart3, RefreshCw } from 'lucide-react';
import { axios, auth, mbUrl, errMsg, fmtMyt, muted, primary, card, ORIGIN_CLS } from './mbApi';
import { Pick } from './TaskFilters';
import { CompareRows, DATA_STATE } from './CompareRows';

const WINDOWS = [['today', 'Hari ini'], ['yesterday', 'Semalam'], ['7', '7 hari'], ['14', '14 hari'], ['30', '30 hari'], ['custom', 'Custom tarikh']];
const mytDay = (daysAgo = 0) => new Date(Date.now() + 8 * 3600000 - daysAgo * 86400000).toISOString().slice(0, 10);
const SORTS = [['cps_first', 'CPS unik (murah dahulu)'], ['cps', 'CPS semua (murah dahulu)'], ['spend', 'Spend (tinggi dahulu)'], ['joins', 'Join unik (banyak dahulu)'], ['label', 'Kod / versi']];
const NONE = { angle: 'all', format: 'all', pic: 'all', state: 'all', sample: 'all' };
const nullLast = (k, dir = 1) => (a, b) => (a[k] == null ? (b[k] == null ? 0 : 1) : b[k] == null ? -1 : (a[k] - b[k]) * dir);
const SORT_FN = {
  cps_first: nullLast('cps_first_myr'), cps: nullLast('cps_myr'), spend: nullLast('amount_spent_myr', -1), joins: nullLast('joins_first', -1),
  label: (a, b) => `${a.code} ${a.version_label}`.localeCompare(`${b.code} ${b.version_label}`),
};
const angleOf = (r) => r.angle_tag || r.angle || '';
const match = (r, f) => (f.angle === 'all' || angleOf(r) === f.angle) && (f.format === 'all' || r.format === f.format)
  && (f.pic === 'all' || r.pic_id === f.pic) && (f.state === 'all' || r.data_state === f.state)
  && (f.sample === 'all' || (f.sample === 'ok') === r.sample_ok);

const Meta = ({ d }) => (
  <div className={`text-sm ${muted} space-y-0.5`} data-testid="mb-compare-meta">
    <p>
      Tempoh: <span className={primary} data-testid="mb-compare-period">{d.since === d.until ? d.since : `${d.since} → ${d.until}`}</span>
      {' · '}hari ikut MYT{d.includes_today ? ' · termasuk hari ini (separa)' : ''}
    </p>
    <p>
      Asal data: <span className={ORIGIN_CLS[d.provenance?.origin]}>{d.provenance?.label}</span>
      {' · '}Auto-sync: <span className={primary}>{d.sync.auto ? 'ON' : 'OFF'}</span>
      {' · '}Last sync berjaya: <span className={primary}>{fmtMyt(d.sync.last_sync_at)}</span>
      {d.sync.stale && <span className="text-amber-400"> · data lapuk</span>}
      {d.sync.status === 'separa' && <span className="text-amber-400"> · sync separa</span>}
    </p>
    <p className="text-xs">{d.notes} Sampel cukup = join unik ≥ {d.cfg.min_joins_first}, spend ≥ RM{d.cfg.min_spend_myr}, ≥ {d.cfg.min_days} hari data.</p>
  </div>
);

const CustomRange = ({ range, setRange }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="mb-compare-custom">
    <span className={`text-xs ${muted}`}>Dari</span>
    <Input type="date" value={range.since} max={range.until || mytDay()} aria-label="Tarikh dari"
      onChange={(e) => setRange({ ...range, since: e.target.value })} className="h-10 w-full sm:w-40" data-testid="mb-compare-since" />
    <span className={`text-xs ${muted}`}>Hingga</span>
    <Input type="date" value={range.until} min={range.since} max={mytDay()} aria-label="Tarikh hingga"
      onChange={(e) => setRange({ ...range, until: e.target.value })} className="h-10 w-full sm:w-40" data-testid="mb-compare-until" />
  </div>
);

export const CompareTable = ({ ibId, members = [] }) => {
  const [win, setWin] = useState('7');
  const [range, setRange] = useState({ since: mytDay(6), until: mytDay() });
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [f, setF] = useState(NONE);
  const [sort, setSort] = useState('cps_first');
  const [showArch, setShowArch] = useState(false);
  const load = useCallback(async () => {
    setErr('');
    setD(null);
    const custom = win === 'custom';
    if (custom && (!range.since || !range.until || range.since > range.until)) { setErr('Pilih tarikh dari & hingga yang sah'); return; }
    const params = custom ? { window: win, since: range.since, until: range.until } : { window: win };
    try { setD((await axios.get(mbUrl(ibId, '/results/compare'), { ...auth(), params })).data); } catch (e) { setErr(errMsg(e, 'Gagal load perbandingan creative')); }
  }, [ibId, win, range]);
  useEffect(() => { load(); }, [load]);
  const all = useMemo(() => (d?.rows || []).filter((r) => showArch || !r.archived), [d, showArch]);
  const rows = useMemo(() => all.filter((r) => match(r, f)).sort(SORT_FN[sort]), [all, f, sort]);
  const uniq = (fn) => [...new Set(all.map(fn).filter(Boolean))].map((v) => [v, v]);
  const nameOf = (id) => members.find((m) => m.id === id)?.name || id;
  const set = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <div className={`${card} space-y-3`} data-testid="mb-compare">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={`text-base font-semibold ${primary} flex items-center gap-2`}><BarChart3 size={17} className="text-[#F77F00]" /> Perbandingan creative</p>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <Pick id="mb-compare-window" label="Tempoh" value={win} onChange={setWin} options={WINDOWS} />
          {win === 'custom' && <CustomRange range={range} setRange={setRange} />}
        </div>
      </div>
      {err && (
        <div className="text-sm text-red-400 space-y-2" data-testid="mb-compare-error">
          <p>{err}</p>
          <Button variant="outline" className="h-10" onClick={load} data-testid="mb-compare-retry"><RefreshCw size={14} className="mr-1.5" /> Cuba lagi</Button>
        </div>
      )}
      {!err && !d && <div className="space-y-2" data-testid="mb-compare-loading">{[1, 2, 3].map((i) => <div key={i} className="h-14 rounded-lg bg-white/[0.04] animate-pulse" />)}</div>}
      {d && (
        <>
          <Meta d={d} />
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2" data-testid="mb-compare-filters">
            <Pick id="mb-compare-sort" label="Susun" value={sort} onChange={setSort} options={SORTS} />
            <Pick id="mb-compare-angle" label="Tapis angle" value={f.angle} onChange={set('angle')} options={[['all', 'Semua angle'], ...uniq(angleOf)]} />
            <Pick id="mb-compare-format" label="Tapis format" value={f.format} onChange={set('format')} options={[['all', 'Semua format'], ...uniq((r) => r.format)]} />
            <Pick id="mb-compare-pic" label="Tapis PIC" value={f.pic} onChange={set('pic')} options={[['all', 'Semua PIC'], ...uniq((r) => r.pic_id).map(([id]) => [id, nameOf(id)])]} />
            <Pick id="mb-compare-state" label="Tapis status data" value={f.state} onChange={set('state')} options={[['all', 'Semua status data'], ...Object.entries(DATA_STATE).map(([k, [l]]) => [k, l])]} />
            <Pick id="mb-compare-sample" label="Tapis sampel" value={f.sample} onChange={set('sample')} options={[['all', 'Semua sampel'], ['ok', 'Sampel cukup'], ['no', 'Belum cukup data']]} />
          </div>
          <div className={`flex flex-wrap items-center justify-between gap-2 text-sm ${muted}`}>
            <span data-testid="mb-compare-count">{rows.length} daripada {all.length} versi · klik baris untuk buka task/versi</span>
            {(d.rows || []).some((r) => r.archived) && (
              <button className="underline min-h-10" onClick={() => setShowArch(!showArch)} data-testid="mb-compare-archived">{showArch ? 'Sembunyi arkib' : 'Tunjuk arkib'}</button>
            )}
          </div>
          {all.length === 0 && <p className={`text-sm ${muted}`} data-testid="mb-compare-empty">Belum ada versi bahan untuk dibandingkan.</p>}
          {all.length > 0 && rows.length === 0 && <p className={`text-sm ${muted}`} data-testid="mb-compare-nomatch">Tiada versi padan dengan tapisan.</p>}
          {rows.length > 0 && <CompareRows rows={rows} />}
        </>
      )}
    </div>
  );
};
